'use strict';

import type { DOMExportOutput, EditorConfig, LexicalEditor, NodeKey, SerializedElementNode, Spread } from 'lexical';
import { ElementNode, isHTMLElement, $applyNodeReplacement } from 'lexical';
import { addClassNamesToElement } from '@lexical/utils';
import { v4 as uuidv4 } from 'uuid';
import dayjs from 'dayjs';
import { getCurrentShowChangesState } from '../context/SharedTrackChangesContext';

function formatDate(date: any) {
  return dayjs(date).toISOString();
}

/** @module @lexical/link */
/** @noInheritDoc */

export type SerializedDeleteNode = Spread<{ username: string; id: string; date: string }, SerializedElementNode>;

export class PhxDeleteNode extends ElementNode {
  /** @internal */
  __username: string;
  __id: string;
  __date: string;

  static getType(): string {
    return 'delete';
  }

  static clone(node: PhxDeleteNode): PhxDeleteNode {
    return new PhxDeleteNode(node.__username, node.__id, node.__date, node.__key);
  }

  constructor(username: string, id?: string, date?: string, key?: NodeKey) {
    super(key);
    this.__username = username;
    if (id === null || id === undefined) {
      this.__id = uuidv4();
    } else {
      this.__id = id;
    }

    if (!date) {
      this.__date = formatDate(new Date());
    } else {
      this.__date = date;
    }
  }

  createDOM(config: EditorConfig): HTMLSpanElement {
    const isEnabled = getCurrentShowChangesState();
    const element = document.createElement('span');
    element.dataset.username = this.__username;
    element.setAttribute('id', this.__id);
    element.dataset.createdAt = this.__date;
    const className = isEnabled ? config.theme.delete : config.theme.deleteDisabled;
    addClassNamesToElement(element, className);
    return element;
  }

  updateDOM(prevNode: this, dom: HTMLElement, config: EditorConfig): boolean {
    // Note: Don't call this.getWritable() here as updateDOM is called during DOM reconciliation
    // and modifying the node state here causes "_cachedNodes" read-only errors
    // The date is already set when the node is created or modified through proper editor updates
    return false;
  }

  static importDOM() {
    return null;
  }

  /**
   * Updates the date of this delete node. Call this within an editor.update() block
   * when you need to explicitly update the timestamp (e.g., when merging nodes).
   */
  setNewDate(): void {
    const writeSelf = this.getWritable();
    writeSelf.__date = formatDate(new Date());
  }

  exportDOM(editor: LexicalEditor): DOMExportOutput {
    const { element } = super.exportDOM(editor);

    if (element && isHTMLElement(element)) {
      if (this.isEmpty()) element.append(document.createElement('br'));

      const formatType = this.getFormatType();
      element.style.textAlign = formatType;

      const direction = this.getDirection();
      if (direction) {
        element.dir = direction;
      }
      const indent = this.getIndent();
      if (indent > 0) {
        // padding-inline-start is not widely supported in email HTML, but
        // Lexical Reconciler uses padding-inline-start. Using text-indent instead.
        element.style.textIndent = `${indent * 20}px`;
      }
      element.setAttribute('id', this.__id);
      element.setAttribute('data-username', this.__username);
      element.setAttribute('data-created-at', this.__date);
      element.className = getCurrentShowChangesState()
        ? editor._config.theme.delete
        : editor._config.theme.deleteDisabled;

      element.setAttribute('date', this.__date);
    }

    return {
      element,
    };
  }

  static importJSON(serializedNode: SerializedDeleteNode): PhxDeleteNode {
    const node = $createDeleteNode(serializedNode.username, serializedNode.id, serializedNode.date);
    node.setFormat(serializedNode.format);
    node.setIndent(serializedNode.indent);
    node.setDirection(serializedNode.direction);
    return node;
  }

  exportJSON() {
    return {
      ...super.exportJSON(),
      type: 'delete',
    };
  }

  canInsertTextBefore() {
    return true;
  }

  canInsertTextAfter() {
    return true;
  }

  canBeEmpty() {
    return false;
  }

  isInline() {
    return true;
  }

  isTextEntity() {
    return true;
  }
}

/**
 * Creates a DeleteNode.
 * @returns DeleteNode.
 */
export function $createDeleteNode(username: string, id?: string, date?: string): PhxDeleteNode {
  return $applyNodeReplacement(new PhxDeleteNode(username, id, date));
}

/**
 * Determines if node is a DeleteNode.
 * @param node - The node to be checked.
 * @returns true if node is a DeleteNode, false otherwise.
 */
export function $isDeleteNode(node: Node) {
  return node instanceof PhxDeleteNode;
}
